import { alpha, styled } from '@mui/material/styles';
import { color, radius, r } from '../../theme/tokens';
import { Paper, Chip, Button, Box, Drawer, Autocomplete } from '@mui/material';

// Styled components
export const SearchContainer = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 10,
  padding: theme.spacing(1),
  margin: 0,
  display: 'flex',
  alignItems: 'center',
  borderRadius: '0.75rem',
  boxShadow: '0 0.125rem 0.5rem rgba(0,0,0,0.08)',
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(0.625rem)',
  width: '100%',
  boxSizing: 'border-box',
  [theme.breakpoints.up('sm')]: {
    margin: theme.spacing(2, 0),
  }
}));

export const CategoryChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  borderRadius: '1.25rem',
  fontWeight: 500,
  height: '2rem',
  transition: 'all 0.2s ease',
  '&.MuiChip-outlined': {
    borderColor: theme.palette.primary.light,
  },
  '&.MuiChip-filled': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  '&:active': {
    transform: 'scale(0.95)',
  }
}));

export const PopularChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  borderRadius: '1.25rem',
  height: '2rem',
  fontSize: '0.875rem',
  transition: 'all 0.2s ease',
  backgroundColor: theme.palette.grey[100],
  '&:hover': {
    backgroundColor: theme.palette.grey[200],
  },
  '&.MuiChip-colorPrimary': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  '&:active': {
    transform: 'scale(0.95)',
  }
}));

export const FilterButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: '1.25rem',
  padding: theme.spacing(0.75, 1.5),
  fontWeight: 500,
  fontSize: '0.875rem',
  boxShadow: 'none',
  '&:active': {
    transform: 'scale(0.98)',
  }
}));

export const SupplementNavItem = styled(Box)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
  '&:active': {
    backgroundColor: theme.palette.primary.light,
    transform: 'scale(0.98)',
  }
}));

// Navigation drawer styled component
export const NavDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: {
      xs: '100%',
      sm: '20rem'
    },
    backgroundColor: theme.palette.background.default,
  }
}));

// Autocomplete styling
export const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  '& .MuiAutocomplete-inputRoot': {
    padding: theme.spacing(0.5, 1),
    '& .MuiAutocomplete-input': {
      padding: theme.spacing(0.5, 0),
    },
  },
  '& .MuiOutlinedInput-root': {
    padding: 0,
    '& fieldset': {
      border: 'none',
    },
  },
  '& .MuiAutocomplete-listbox': {
    padding: '0.25rem 0',
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 0.125rem 0.625rem rgba(0,0,0,0.08)',
    borderRadius: '0.5rem',
    '& .MuiAutocomplete-option': {
      minHeight: 'auto',
      padding: 0,
    },
  },
}));

// Suggestion item styling
export const SuggestionItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  cursor: 'pointer',
  transition: 'background-color 0.15s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  borderBottom: '0.0625rem solid',
  borderColor: 'rgba(0, 0, 0, 0.04)',
  '&:last-child': {
    borderBottom: 'none',
  },
}));

// Main styles object
export const styles = {
  root: {
    width: '100%',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: 'background.default',
  },
  contentContainer: {
    width: '100%',
    maxWidth: '75rem',
    margin: '0 auto',
    padding: { 
      xs: '0.75rem 1rem', 
      sm: '1rem 1.25rem', 
      md: '1.25rem 1.5rem', 
      lg: '1.5rem 2rem' 
    },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  header: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '1.5rem',
  },
  headerTitle: {
    fontSize: { xs: '1.5rem', sm: '2rem' },
    fontWeight: 600,
    color: 'text.primary',
    marginBottom: '1.5rem',
    textAlign: 'center',
  },
  searchBar: {
    width: { xs: '100%', sm: '75%', md: '65%' },
    maxWidth: '37.5rem',
    marginBottom: '1.5rem',
    '& .MuiOutlinedInput-root': {
      minHeight: { xs: '3.45rem', sm: '3.2rem' },
      borderRadius: '1rem',
      backgroundColor: 'background.paper',
      boxShadow: `0 0.22rem 0.9rem ${alpha(color.evergreen, 0.08)}`,
      '& fieldset': {
        borderColor: alpha(color.evergreen, 0.22),
        borderWidth: '0.0625rem',
      },
      '&:hover': {
        boxShadow: `0 0.3rem 1rem ${alpha(color.evergreen, 0.11)}`,
        '& fieldset': {
          borderColor: alpha(color.evergreen, 0.34),
        },
      },
      '&.Mui-focused': {
        boxShadow: `0 0.35rem 1.1rem ${alpha(color.evergreen, 0.14)}`,
        '& fieldset': {
          borderColor: color.accent,
          borderWidth: '0.09375rem',
        },
      },
      '& .MuiOutlinedInput-input': {
        fontSize: '1.1rem',
        color: 'text.primary',
      },
      '& .MuiInputAdornment-root .MuiSvgIcon-root': {
        color: alpha(color.evergreen, 0.75),
        fontSize: '1.35rem',
      },
    },
  },
  searchChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    marginBottom: '2rem',
    justifyContent: 'center',
    width: '100%',
  },
  supplementsSection: {
    width: '100%',
    mb: 3,
    mt: 0.5,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  supplementChips: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: '0.3125rem',
    overflowX: 'auto',
    overflowY: 'hidden',
    padding: '0.1875rem 0',
    width: '100%',
    '&::-webkit-scrollbar': {
      display: 'none'
    },
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    position: 'relative',
    marginBottom: '0.25rem',
    '&::after': {
      content: '""',
      position: 'absolute',
      right: 0,
      top: 0,
      height: '100%',
      width: '3rem',
      background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.95))',
      pointerEvents: 'none',
      opacity: 0.8,
    }
  },
  supplementSection: {
    width: '100%',
    marginBottom: '3rem',
    marginTop: '1rem',
  },
  supplementTitle: {
    width: '100%',
    fontSize: '1rem',
    fontWeight: 600,
    color: 'text.primary',
    marginBottom: '1rem',
    padding: '0.625rem 1rem',
    backgroundColor: 'rgba(245, 247, 250, 0.5)',
    borderRadius: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxSizing: 'border-box',
    boxShadow: '0 0.0625rem 0.125rem rgba(0,0,0,0.02)',
  },
  supplementCount: {
    fontSize: '0.75rem',
    color: 'text.secondary',
    fontWeight: 400,
  },
  productsGrid: {
    display: 'grid',
    gridTemplateColumns: {
      xs: 'repeat(auto-fill, minmax(16.875rem, 1fr))',
      sm: 'repeat(auto-fill, minmax(18.125rem, 1fr))',
      md: 'repeat(auto-fill, minmax(15.625rem, 1fr))',
      lg: 'repeat(auto-fill, minmax(15rem, 1fr))',
    },
    gap: {
      xs: '1rem',
      sm: '1.25rem',
      md: '1.5rem'
    },
    width: '100%',
    marginTop: '1.5rem',
    padding: {
      xs: '0 0.125rem',
      sm: '0 0.25rem',
      md: '0'
    }
  },
  productCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '0.75rem',
    transition: 'all 0.25s ease-in-out',
    backgroundColor: 'background.paper',
    boxShadow: '0 0.125rem 0.5rem rgba(0,0,0,0.05)',
    border: '0.0625rem solid rgba(0,0,0,0.03)',
    overflow: 'hidden',
    margin: { xs: '0', sm: '0 0.125rem' },
    '&:hover': {
      transform: 'translateY(-0.375rem)',
      boxShadow: '0 0.75rem 1.5rem rgba(0,0,0,0.09)',
    },
  },
  productImage: {
    height: '15rem',
    objectFit: 'contain',
    backgroundColor: 'grey.50',
    padding: '1rem',
  },
  productContent: {
    flexGrow: 1,
    padding: '1.25rem',
  },
  productBrand: {
    fontWeight: 600,
    color: 'text.primary',
    marginBottom: '0.25rem',
  },
  productTitle: {
    color: 'text.secondary',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    lineHeight: 1.4,
  },
  productRating: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '0.75rem',
    marginBottom: '1rem',
  },
  productPrice: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: 'primary.main',
  },
  productActions: {
    padding: '1rem',
    borderTop: '0.0625rem solid',
    borderColor: 'divider',
  },
  viewButton: {
    borderRadius: '0.5rem',
    textTransform: 'none',
    fontWeight: 600,
    padding: '0.5rem 1rem',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '3.75rem 0',
  },
  noResults: {
    textAlign: 'center',
    padding: '3.75rem 0',
    backgroundColor: 'grey.50',
    borderRadius: '1rem',
  },
  filterContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    mt: { xs: 2, sm: 3 },
    mb: 5,
    position: 'relative',
  },
  filterHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    mb: 2,
  },
  filterButton: {
    minWidth: 'unset',
    borderRadius: radius.sm,
    padding: `${r(6)} ${r(12)}`,
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    backgroundColor: theme => theme.palette.grey[50],
    '&:hover': {
      backgroundColor: theme => theme.palette.grey[100],
    },
  },
  productsHeader: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 3,
  },
  productsTitle: {
    fontSize: { xs: '0.875rem', sm: '1rem' },
    fontWeight: 600,
    color: 'text.primary',
    letterSpacing: '0.015em',
  },
  productsCount: {
    fontSize: '0.875rem',
    color: 'text.secondary',
  },
}; 